import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useState,
} from "react";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VariationSelection } from "../../../../common/types/apiTypes";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { ShiftDetailsType } from "../../additionalInfo/types";
import {
  Automatching,
  Bundling,
  bundlingDefaultValues,
  HandleChangeValue,
  OrderAdditionalInfo,
  OrderFields,
  OrderRow,
  shortOrderDefaultFieldValues,
  ValidationError,
} from "../../orderTypes";
import * as orderRowHandlers from "./orderFormShortRowHandlers";

type Props = {
  children: ReactElement;
};

interface ShiftOrderContextValues {
  orderRows: OrderRow[];
  bundling: Bundling;
  orderAdditionalInfo: OrderAdditionalInfo;
  validationErrors: ValidationError[];
  setValidationErrors: Dispatch<SetStateAction<ValidationError[]>>;
  setBundling: Dispatch<SetStateAction<Bundling>>;
  isAdditionalInfoChanged: boolean;
  setIsAdditionalInfoChanged: (value: boolean) => void;
  handleAddRow: () => void;
  handleDuplicateBundledRow: (rowId: string, id: string) => void;
  handleDuplicateRow: (owId: string) => void;
  handleDeleteRow: (rowId: string, id?: string, bundleRowId?: string) => void;
  resetBundlingState: () => void;
  toggleRowBundling: (
    rowId: string,
    rowItemId: string,
    operation: "add" | "remove",
  ) => void;
  handleChange: (
    name: string,
    value: HandleChangeValue,
    rowId: string,
    fieldId: string,
    isFirstRow?: boolean,
  ) => void;
  handleWorkLocationWithReset: (
    workLocation: any,
    rowId: string,
    fieldId: string,
    isFirstRow?: boolean,
  ) => void;
  handleOrderTemplateChange: (
    name: string,
    value: string | null | AutocompleteType | AutocompleteType[],
    rowId: string,
  ) => void;
  handleError: (error: boolean, validationError: ValidationError) => void;

  handleAddNewRowToBundle: (bundleRowId: string) => void;
  handleAddBundleContainer: () => void;
  handleRemoveBundleContainer: (bundleId: string) => void;
  handleToggleAdditionalInfo: (id?: string, rowId?: string) => void;
  setTesVariation: (variation: VariationSelection[], rowId: string) => void;
  handleAdditionalInfoChange: (
    id: string,
    values: OrderFields,
    shiftDetails: ShiftDetailsType[],
    bundleId?: string,
  ) => void;
}

const NON_COPYABLE_FIELDS = ["date", "time", "customerCostCenter"];

// @ts-ignore
export const ShiftOrderContext = createContext<ShiftOrderContextValues>();

// REVIEW: components could handle most of the grunt work when editing different sections of the orderRow

const OrderShortDataProvider = ({ children }: Props) => {
  const [orderRows, setOrderRows] = useState<OrderRow[]>([]);
  const [bundling, setBundling] = useState<Bundling>(bundlingDefaultValues);
  const [orderAdditionalInfo, setOrderAdditionalInfo] =
    useState<OrderAdditionalInfo>({ show: false, rowId: "", id: "" });
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [isAdditionalInfoChanged, setIsAdditionalInfoChanged] = useState(false);

  const handleAddRow = () => {
    const newRow = orderRowHandlers.createNewSingleRow();
    setOrderRows([...orderRows, newRow]);
  };

  const handleDuplicateBundledRow = (rowId: string, id: string) =>
    setOrderRows(orderRowHandlers.duplicateBundledRow(orderRows, rowId, id));

  const handleDuplicateRow = (rowId: string) =>
    setOrderRows(orderRowHandlers.duplicateRow(orderRows, rowId, handleError));

  const resetBundlingState = () => setBundling(bundlingDefaultValues);

  const handleDeleteRow = (
    rowId: string,
    id?: string,
    bundleRowId?: string,
  ) => {
    if (id) {
      // Clean up validation errors for the deleted row
      setValidationErrors((currentErrors) =>
        currentErrors.filter((error) => error.id !== id),
      );

      return setOrderRows(
        orderRowHandlers.deleteBundledSingleRow(rowId, id, orderRows),
      );
    }

    if (bundleRowId) {
      if (bundling.bundleId === bundleRowId) {
        resetBundlingState();
      }

      // Clean up validation errors for all rows in the deleted bundle
      const bundleRow = orderRows.find((row) => row.bundleId === bundleRowId);
      if (bundleRow) {
        const bundleRowIds = bundleRow.rows.map((row) => row.id);
        setValidationErrors((currentErrors) =>
          currentErrors.filter(
            (error) => error.id !== null && !bundleRowIds.includes(error.id),
          ),
        );
      }

      return setOrderRows(
        orderRowHandlers.deleteBundleRow(orderRows, bundleRowId),
      );
    }

    setOrderRows(orderRowHandlers.deleteSingleRow(rowId, orderRows));
  };

  const getOrderTemplateWithSalaryLimit = (
    orderTemplate: OrderFields["orderTemplate"],
    date: Date | null,
  ) => ({
    ...orderTemplate,
    salaryLimit: orderRowHandlers.determineSalaryLimit(orderTemplate, date),
  });

  const getUpdatedFieldValues = (
    fields: OrderFields,
    name: string,
    value: HandleChangeValue,
  ): OrderFields => {
    if (name === "orderTemplate") {
      return {
        ...fields,
        orderTemplate: getOrderTemplateWithSalaryLimit(
          value as OrderFields["orderTemplate"],
          fields.date,
        ),
      };
    }

    if (name === "date") {
      const date = value as Date | null;
      return {
        ...fields,
        date,
        orderTemplate: getOrderTemplateWithSalaryLimit(fields.orderTemplate, date),
      };
    }

    return {
      ...fields,
      [name]: value,
    };
  };

  const updateToMultipleRows = (
    name: string,
    value: HandleChangeValue,
    rowId: string,
  ) => {
    const rows = orderRows.find((row) => row.rowId === rowId)?.rows;
    const updatedRows = rows?.map((fieldValues) =>
      getUpdatedFieldValues(fieldValues, name, value),
    );

    if (rows && updatedRows) {
      return setOrderRows(
        orderRows.map((row) =>
          row.rowId === rowId
            ? {
                ...row,
                rows: [...updatedRows],
              }
            : row,
        ),
      );
    }
  };

  const handleAutomatchingChange = (
    name: string,
    value: Automatching,
    rowId: string,
    id?: string,
  ) => {
    if (id) {
      // bundle
      updateToMultipleRows(name, value, rowId);
    } else {
      // signle row
      return setOrderRows(
        orderRows.map((row) => ({
          ...row,
          rows: row.rows.map((fields) => {
            return row.rowId === rowId ? { ...fields, [name]: value } : fields;
          }),
        })),
      );
    }
  };

  const toggleRowBundling = (
    rowId: string,
    rowItemId: string,
    operation: "add" | "remove",
  ) => {
    const bundleContainerRow = orderRows.find(
      (row) => row.bundleId === bundling.bundleId,
    ) as OrderRow;

    // change a bundled row to a single row and delete the existing bundled row
    if (operation === "remove") {
      const newBundleContainer = orderRowHandlers.removeFromBundle(
        bundleContainerRow as OrderRow,
        rowItemId,
      );
      const newSingleRow = orderRowHandlers.transformToSingleRow(
        bundleContainerRow?.rows.find(
          (row) => row.id === rowItemId,
        ) as OrderFields,
      );

      setOrderRows(
        [newSingleRow, ...orderRows].map((row) =>
          row.rowId === bundleContainerRow?.rowId ? newBundleContainer : row,
        ),
      );

      if (bundleContainerRow.rows.length === 1) {
        setBundling({
          ...bundling,
          bundleTemplate: null,
          bundleWorkLocation: null,
          bundleAmount: null,
          bundleReasonForTemporaryEmployment: null,
        });
      }
    }

    // delete the existing single row and add it to bundle
    if (operation === "add") {
      const existingRow = orderRows.find(
        (row) => row.rowId === rowId,
      ) as OrderRow;

      const bundleContainsManuallyAddedTalent = bundleContainerRow.rows.some(
        (row) => !isEmpty(row.automatching.talents),
      );
      const existingRowContainsManuallyAddedTalent = existingRow.rows.some(
        (row) => !isEmpty(row.automatching.talents),
      );
      const resetManuallyAddedTalents =
        bundleContainsManuallyAddedTalent ||
        existingRowContainsManuallyAddedTalent;

      const bundleContainsTimeBasedNotification = bundleContainerRow.rows.some(
        (row) => !!row.notifyMinutesBeforeIfNotFulfilled,
      );
      const existingRowContainsTimeBasedNotification = existingRow.rows.some(
        (row) => !!row.notifyMinutesBeforeIfNotFulfilled,
      );
      const resetTimeBasedNotification =
        bundleContainsTimeBasedNotification ||
        existingRowContainsTimeBasedNotification;

      setBundling({
        ...bundling,
        resetManuallyAddedTalents,
        resetTimeBasedNotification,
      });

      // REVIEW: setting orderRows should be unified
      setOrderRows(
        orderRows
          .filter((row) => row.rowId !== rowId)
          .map((row) =>
            row.rowId === bundleContainerRow.rowId
              ? orderRowHandlers.addToBundle(bundleContainerRow, existingRow)
              : row,
          ),
      );
    }
  };

  const handleChange = (
    name: string,
    value: HandleChangeValue,
    rowId: string,
    fieldId: string,
    isFirstRow?: boolean,
  ) => {
    setIsAdditionalInfoChanged(false);

    const bundleId = orderRows.find((row) => row.rowId === rowId)?.bundleId;
    const id = !!bundleId ? fieldId : undefined;

    if (name === "automatching") {
      const typedValue = value as Automatching;
      return handleAutomatchingChange(name, typedValue, rowId, id);
    }

    // copy values from first row of the bundle to the rest of the bundled rows
    if (isFirstRow && id && !NON_COPYABLE_FIELDS.includes(name)) {
      return updateToMultipleRows(name, value, rowId);
    }

    return setOrderRows(
      orderRows.map((row) => ({
        ...row,
        rows: row.rows.map((fields) => {
          if (id) {
            // bundled row
            return fields.id === id
              ? getUpdatedFieldValues(fields, name, value)
              : fields;
          }
          // single row
          return row.rowId === rowId
            ? getUpdatedFieldValues(fields, name, value)
            : fields;
        }),
      })),
    );
  };

  const handleWorkLocationWithReset = (
    workLocation: any,
    rowId: string,
    fieldId: string,
    isFirstRow?: boolean,
  ) => {
    const bundleId = orderRows.find((row) => row.rowId === rowId)?.bundleId;
    const id = !!bundleId ? fieldId : undefined;

    // Create empty orderTemplate compatible with the new workLocation
    const resetOrderTemplate = shortOrderDefaultFieldValues.orderTemplate;

    // For bundle first row: copy workLocation and reset orderTemplate/customerCostCenter for ALL bundle rows
    if (isFirstRow && id) {
      return setOrderRows(
        orderRows.map((row) => ({
          ...row,
          rows: row.rows.map((fields) => {
            if (row.rowId === rowId) {
              return {
                ...fields,
                workLocation,
                customerCostCenter: { id: "", name: "" },
                orderTemplate: resetOrderTemplate,
              };
            }
            return fields;
          }),
        })),
      );
    }

    // Update workLocation, reset customerCostCenter and orderTemplate to defaults/empty
    return setOrderRows(
      orderRows.map((row) => ({
        ...row,
        rows: row.rows.map((fields) => {
          if (id) {
            // bundled row
            return fields.id === id
              ? {
                  ...fields,
                  workLocation,
                  customerCostCenter: { id: "", name: "" },
                  orderTemplate: resetOrderTemplate,
                }
              : fields;
          }
          // single row
          return row.rowId === rowId
            ? {
                ...fields,
                workLocation,
                customerCostCenter: { id: "", name: "" },
                orderTemplate: resetOrderTemplate,
              }
            : fields;
        }),
      })),
    );
  };

  //REVIEW: naming handles the change that change
  //values from the template not template change
  //itself
  const handleOrderTemplateChange = (
    name: string,
    value: string | null | AutocompleteType | AutocompleteType[],
    rowId: string,
  ) => {
    const updatedRows = orderRows.map((row) =>
      row.rowId === rowId
        ? {
            ...row,
            rows: row.rows.map((fields) => {
              return {
                ...fields,
                orderTemplate: {
                  ...fields.orderTemplate,
                  [name]: value,
                },
              };
            }),
          }
        : row,
    );

    return setOrderRows(updatedRows);
  };

  const handleError = (error: boolean, validationError: ValidationError) => {
    setValidationErrors((currentErrors) => {
      const existingError = currentErrors.find(
        (err) =>
          err.id === validationError.id && err.field === validationError.field,
      );

      if (!existingError && error) {
        return [...currentErrors, validationError];
      }

      if (existingError && !error) {
        return currentErrors.filter(
          (err) =>
            !(
              err.id === validationError.id &&
              err.field === validationError.field
            ),
        );
      }

      if (existingError && error) {
        return currentErrors.map((err) =>
          err.id === validationError.id && err.field === validationError.field
            ? validationError
            : err,
        );
      }

      if (!existingError && !error) {
        return currentErrors;
      }

      return currentErrors;
    });
  };

  // vuoro button in the bundle
  const handleAddNewRowToBundle = (bundleRowId: string) =>
    setOrderRows(
      orderRows.map((row) =>
        row.bundleId === bundleRowId
          ? {
              ...row,
              rows: [
                ...row.rows,
                // reset date, time and customerCostCenter, copy rest of the values from first row
                // or add empty values as the first row
                orderRowHandlers.createNewRowToBundle(
                  !!row.rows.length
                    ? row.rows[0]
                    : shortOrderDefaultFieldValues,
                ),
              ],
            }
          : row,
      ),
    );

  // vuoronippu button
  const handleAddBundleContainer = () => {
    const bundleContainer = orderRowHandlers.createNewBundleContainer();

    setOrderRows([...orderRows, bundleContainer]);
    setBundling({
      show: true,
      bundleId: bundleContainer.bundleId,
      bundleTemplate: null,
      bundleWorkLocation: null,
      bundleReasonForTemporaryEmployment: null,
      bundleAmount: null,
      resetManuallyAddedTalents: false,
      resetTimeBasedNotification: false,
    });
  };

  const handleRemoveBundleContainer = (bundleId: string) => {
    setOrderRows(orderRows.filter((row) => row.bundleId !== bundleId));
    resetBundlingState();
  };

  const handleToggleAdditionalInfo = (id?: string, rowId?: string) => {
    if (id && rowId) {
      return setOrderAdditionalInfo({ show: true, id, rowId });
    }

    setOrderAdditionalInfo({ show: false, id: "", rowId: "" });
  };

  const setTesVariation = (variation: VariationSelection[], rowId: string) => {
    // TODO: JSON type
    let newRow = getOrderRow(rowId);
    newRow.rows = newRow.rows.map((row) => {
      return {
        ...row,
        orderTemplate: {
          ...row.orderTemplate,
          tesVariation: variation,
        },
      };
    });

    setOrderRow({ ...newRow });
  };

  const setOrderRow = (newRow: OrderRow) => {
    setOrderRows(
      orderRows.map((row) => (row.rowId === newRow.rowId ? newRow : row)),
    );
  };

  const getOrderRow = (id: string): OrderRow => {
    return orderRows.find((row) => row.rowId === id) as OrderRow;
  };

  const handleAdditionalInfoChange = (
    rowId: string,
    formValues: OrderFields,
    shiftDetails: ShiftDetailsType[],
    bundleId?: string,
  ) => {
    setIsAdditionalInfoChanged(true);

    let newRow = getOrderRow(rowId);

    if (bundleId) {
      const newRows = [
        ...newRow.rows.map((row) => {
          const shiftDetailRow = shiftDetails.find(
            (shiftDetail) => shiftDetail.id === row.id,
          );

          return shiftDetailRow
            ? {
                ...formValues,
                ...shiftDetailRow,
                createdAt: row.createdAt,
              }
            : row;
        }),
      ];

      newRow.rows = newRows;
    } else {
      const updatedRow = {
        ...formValues,
        time: shiftDetails[0].time,
        customerCostCenter: shiftDetails[0].customerCostCenter,
        date: shiftDetails[0].date,
        isFillShift: shiftDetails[0].isFillShift,
      };

      newRow.rows = [updatedRow];
    }
    setOrderRow(newRow);
  };

  return (
    <ShiftOrderContext.Provider
      value={{
        orderRows,
        bundling,
        orderAdditionalInfo,
        validationErrors,
        setValidationErrors,
        setBundling,
        isAdditionalInfoChanged,
        setIsAdditionalInfoChanged,
        setTesVariation,
        handleAddRow,
        handleDuplicateBundledRow,
        handleDuplicateRow,
        handleDeleteRow,
        resetBundlingState,
        toggleRowBundling,
        handleChange,
        handleWorkLocationWithReset,
        handleOrderTemplateChange,
        handleError,
        handleAddNewRowToBundle,
        handleAddBundleContainer,
        handleRemoveBundleContainer,
        handleToggleAdditionalInfo,
        handleAdditionalInfoChange,
      }}
    >
      {children}
    </ShiftOrderContext.Provider>
  );
};

export default OrderShortDataProvider;
